<template>
  <div class="pay-third-pie-chart">
    <div v-if="loading" class="chart-loading">
      <el-icon class="is-loading"><i class="el-icon-loading"></i></el-icon>
      <span>数据加载中...</span>
    </div>
    <div v-else-if="!hasData" class="chart-empty">
      <i class="el-icon-pie-chart"></i>
      <span>暂无数据</span>
    </div>
    <div v-else ref="chart" class="chart-container"></div>
  </div>
</template>

<script>
import echarts from 'echarts'

export default {
  name: 'pay-third-pie-chart',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      chart: null,
      resizeHandler: null
    }
  },

  computed: {
    hasData() {
      return this.data && this.data.length > 0
    },

    chartData() {
      if (!this.hasData) return []
      // 按订单数量从大到小排序
      return this.data.filter(item => item.times && Number(item.times) > 0)
        .map(item => ({
          name: item.name,
          value: Number(item.times),
          amount: Number(item.amount || 0)
        }))
        .sort((a, b) => b.value - a.value)
    }
  },

  watch: {
    data: {
      handler() {
        this.$nextTick(() => {
          if (this.hasData && !this.chart) {
            this.initChart()
          } else if (this.chart) {
            this.renderChart()
          }
        })
      },
      deep: true
    },

    loading(newVal) {
      if (!newVal && this.hasData) {
        this.$nextTick(() => {
          if (!this.chart) {
            this.initChart()
          } else {
            this.renderChart()
          }
        })
      }
    }
  },

  mounted() {
    this.initChart()
    this.bindEvents()
  },

  beforeDestroy() {
    this.unbindEvents()
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },

  methods: {
    initChart() {
      if (!this.$refs.chart) return
      this.chart = echarts.init(this.$refs.chart)
      this.renderChart()
    },

    renderChart() {
      if (!this.hasData) {
        if (this.chart) {
          this.chart.dispose()
          this.chart = null
        }
        return
      }

      if (!this.chart) return

      const colorMap = {
        '支付宝': '#1677ff',
        '微信支付': '#52c41a',
        '工商银行': '#ff4d4f',
        '农业银行': '#13c2c2',
        '交通银行': '#722ed1',
        '建设银行': '#fa8c16'
      }

      // 计算图例高度,合理利用空间
      const chartHeight = 320
      const itemHeight = 14
      const itemGap = 12
      const legendPadding = 40
      const maxLegendItems = Math.floor((chartHeight - legendPadding) / (itemHeight + itemGap))

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: (params) => {
            return `${params.name}<br/>订单数: ${params.value}<br/>占比: ${params.percent}%`
          }
        },
        legend: {
          type: this.chartData.length > maxLegendItems ? 'scroll' : 'plain', // 根据数量决定是否滚动
          orient: 'vertical',
          right: 20,
          top: 'center',
          bottom: 20,
          itemWidth: 14,
          itemHeight: 14,
          itemGap: 12,
          textStyle: {
            fontSize: 12,
            color: '#666'
          },
          pageButtonItemGap: 10,
          pageButtonPosition: 'end',
          pageFormatter: '{current}/{total}',
          pageIconColor: '#667eea',
          pageIconInactiveColor: '#aaa',
          pageIconSize: 12,
          pageTextStyle: {
            color: '#666',
            fontSize: 11
          },
          pageButtonGap: 5,
          formatter: (name) => {
            const item = this.chartData.find(d => d.name === name)
            if (item) {
              const total = this.chartData.reduce((sum, d) => sum + d.value, 0)
              const percent = ((item.value / total) * 100).toFixed(1)
              return `${name}  ${percent}%`
            }
            return name
          }
        },
        series: [{
          type: 'pie',
          radius: ['45%', '70%'],
          center: ['35%', '50%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: true,
            position: 'outside',
            formatter: '{d}%',
            fontSize: 12,
            color: '#666',
            fontWeight: 'bold'
          },
          labelLine: {
            show: true,
            length: 15,
            length2: 10,
            smooth: true,
            lineStyle: {
              color: '#999',
              width: 1
            }
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '16',
              fontWeight: 'bold'
            },
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          data: this.chartData.map(item => ({
            name: item.name,
            value: item.value,
            itemStyle: {
              color: colorMap[item.name] || '#667eea'
            }
          }))
        }]
      }

      this.chart.setOption(option, true)
    },

    bindEvents() {
      this.resizeHandler = () => {
        if (this.chart) {
          this.chart.resize()
        }
      }
      window.addEventListener('resize', this.resizeHandler)
    },

    unbindEvents() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
    }
  }
}
</script>

<style scoped>
.pay-third-pie-chart {
  height: 100%;
  position: relative;
  background: linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%);
  border-radius: 12px;
  padding: 8px;
}

.chart-container {
  width: 100%;
  height: 100%;
  min-height: 350px;
}

.chart-loading,
.chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 320px;
  color: #909399;
  font-size: 14px;
}

.chart-loading i,
.chart-empty i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.5;
}
</style>




// WEBPACK FOOTER //
// src/custom/components/stats/pay-stats/components/pay-third-pie-chart.vue